import { z } from 'zod';

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
  .refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, 'Expected a valid calendar date');
const positiveCents = z.number().int().positive();

export const financialClasses = [
  'OPERATING_INCOME',
  'OPERATING_EXPENSE',
  'INTEREST',
  'PRINCIPAL',
  'CAPITAL_EXPENDITURE',
  'OTHER',
  'NON_CASH',
  'UNCLASSIFIED',
] as const;
export type FinancialClass = (typeof financialClasses)[number];

export const createInvestmentValuationSchema = z.object({
  amount: positiveCents,
  valuationDate: isoDate,
  source: z.string().trim().min(1).max(200),
});
export const updateInvestmentValuationSchema = createInvestmentValuationSchema.partial();
export type CreateInvestmentValuationRequestBody = z.infer<typeof createInvestmentValuationSchema>;
export type UpdateInvestmentValuationRequestBody = z.infer<typeof updateInvestmentValuationSchema>;

export interface InvestmentValuationDTO extends CreateInvestmentValuationRequestBody {
  id: string;
  investmentId: string;
  createdAt: string;
  updatedAt: string;
}

export interface InvestmentFinancingEstimateDTO {
  source: 'ESTIMATED';
  asOf: string;
  originalLoanAmount: number;
  outstandingBalance: number;
  annualRate: number;
  estimatedMonthlyPayment: number;
  estimatedAnnualInterest: number;
  estimatedAnnualPrincipal: number;
}

export const updateFinancialClassificationSchema = z.object({
  financialClass: z.enum(financialClasses),
});
export type UpdateFinancialClassificationRequestBody = z.infer<
  typeof updateFinancialClassificationSchema
>;

export const financialMetricsQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  asOf: isoDate.optional(),
});

export type MetricQuality = 'RECORDED' | 'PROVISIONAL' | 'UNAVAILABLE';
export interface MetricValueDTO {
  value: number | null;
  quality: MetricQuality;
  reason?: string;
}

export interface InvestmentAnnualEstimateDTO {
  source: 'INVESTMENT_INPUTS';
  asOf: string;
  operatingIncome: MetricValueDTO;
  operatingExpenses: MetricValueDTO;
  cashNoi: MetricValueDTO;
  interestPaid: MetricValueDTO;
  principalRepaid: MetricValueDTO;
  debtServicePaid: MetricValueDTO;
  cashflowAfterDebt: MetricValueDTO;
  returnOnMarketValue: MetricValueDTO;
  yieldOnCost: MetricValueDTO;
}

export interface InvestmentActualAnnualizedDTO {
  source: 'RECORDED_RUN_RATE';
  observationStartDate: string;
  observationEndDate: string;
  observedDays: number;
  projectedDays: number;
  operatingIncome: MetricValueDTO;
  operatingExpenses: MetricValueDTO;
  cashNoi: MetricValueDTO;
  interestPaid: MetricValueDTO;
  principalRepaid: MetricValueDTO;
  debtServicePaid: MetricValueDTO;
  cashflowAfterDebt: MetricValueDTO;
  returnOnMarketValue: MetricValueDTO;
  yieldOnCost: MetricValueDTO;
}

export interface InvestmentEquityMetricsDTO {
  operatingBasis: 'ACTUAL_PERIOD' | 'ANNUAL_ESTIMATE';
  initialCashInvested: MetricValueDTO;
  currentEquity: MetricValueDTO;
  beginningEquity: MetricValueDTO;
  periodCashflowAfterDebt: MetricValueDTO;
  cashflowAfterDebt: MetricValueDTO;
  cashOnCash: MetricValueDTO;
  cashYieldOnCurrentEquity: MetricValueDTO;
  operatingRoe: MetricValueDTO;
  appreciation: MetricValueDTO;
  totalRoe: MetricValueDTO;
}

export interface InvestmentFinancialMetricsDTO {
  investmentId: string;
  period: {
    year: number;
    startDate: string;
    endDate: string;
    asOf: string;
    isYearToDate: boolean;
    annualized: false;
  };
  ownershipPercentage: number;
  amountsScope: 'FULL_ASSET';
  movementDataStatus: 'RECORDED';
  actualAnnualized: InvestmentActualAnnualizedDTO | null;
  annualEstimate: InvestmentAnnualEstimateDTO | null;
  valuation: InvestmentValuationDTO | null;
  beginningValuation: InvestmentValuationDTO | null;
  financing: InvestmentFinancingEstimateDTO | null;
  initialCost: {
    amount: number;
    complete: boolean;
  };
  metrics: {
    operatingIncome: MetricValueDTO;
    operatingExpenses: MetricValueDTO;
    cashNoi: MetricValueDTO;
    returnOnMarketValue: MetricValueDTO;
    yieldOnCost: MetricValueDTO;
    interestPaid: MetricValueDTO;
    principalRepaid: MetricValueDTO;
    debtServicePaid: MetricValueDTO;
    cashflowAfterDebt: MetricValueDTO;
    capexPaid: MetricValueDTO;
    cashflowAfterCapex: MetricValueDTO;
    otherReceipts: MetricValueDTO;
    otherPayments: MetricValueDTO;
  };
  operatingExpensesByCategory: Record<string, number>;
  vacancy: {
    availableDays: number;
    vacantDays: number;
    estimatedLostRent: number;
    coverage: 'NONE' | 'INFERRED';
    monetaryValueIsEstimate: true;
  };
  equityMetrics: InvestmentEquityMetricsDTO;
  quality: {
    unclassifiedLineCount: number;
    notes: string[];
  };
}

export type InvestmentValuationListResponse = { data: InvestmentValuationDTO[] };
export type InvestmentFinancialMetricsResponse = { data: InvestmentFinancialMetricsDTO };
